import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { theme } from '../../theme';
import { apiRequest } from '../../utils/apiUtils';
import {
  ArrowLeft,
  Lock,
  CreditCard,
  Wallet,
  Smartphone,
  CheckCircle,
  ShieldCheck,
  Calendar,
  Clock,
  MapPin,
} from 'lucide-react';
import { VenueBooking, Venue } from '../../types/venue';
import { formatPrice } from '../../utils/venueHelpers';

interface PaymentPageData {
  booking: VenueBooking & { venue: Venue };
  preferenceId: string;
  initPoint: string;
}

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');

  const [data, setData] = useState<PaymentPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    if (!bookingId) {
      navigate('/venues');
      return;
    }

    const fetchPaymentData = async () => {
      try {
        setLoading(true);
        const response = await apiRequest(`/api/bookings/${bookingId}/payment/init`);

        if (response.data) {
          setData(response.data);
        } else {
          setError('No se pudo inicializar el pago');
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching payment data:', err);
        setError('Error al cargar los datos de pago');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, [bookingId, navigate]);

  const handlePayWithMercadoPago = () => {
    if (!data?.initPoint) return;

    setProcessingPayment(true);

    // Redirigir a Mercado Pago
    window.location.href = data.initPoint;
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    padding: '32px 48px',
    borderBottom: `1px solid ${theme.colors.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const backButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.button,
    cursor: 'pointer',
    color: theme.colors.textSecondary,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '32px',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '4px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '14px',
    color: theme.colors.textSecondary,
  };

  const securityBadgeStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: theme.colors.background,
    padding: '8px 16px',
    borderRadius: '8px',
  };

  const contentStyle: React.CSSProperties = {
    padding: '48px',
    display: 'flex',
    gap: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  };

  const leftColumnStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const rightColumnStyle: React.CSSProperties = {
    width: '480px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '24px',
  };

  const cardTitleStyle: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.primary,
    fontFamily: theme.fonts.heading,
    letterSpacing: '0.5px',
    marginBottom: '20px',
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const infoValueStyle: React.CSSProperties = {
    fontSize: '16px',
    color: theme.colors.text,
    fontWeight: theme.fonts.weightNormal,
  };

  const dividerStyle: React.CSSProperties = {
    height: '1px',
    backgroundColor: theme.colors.border,
    margin: '16px 0',
  };

  const mpButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '20px',
    backgroundColor: '#009EE3',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: theme.borderRadius.card,
    fontSize: '16px',
    fontWeight: theme.fonts.weightBold,
    cursor: processingPayment ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    transition: 'all 0.2s',
    opacity: processingPayment ? 0.6 : 1,
  };

  const methodCardStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: theme.colors.background,
    padding: '16px',
    borderRadius: theme.borderRadius.button,
  };

  const featureStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.colors.textSecondary,
    fontSize: '13px',
  };

  if (loading) {
    return (
      <div style={{ ...containerStyle, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <p style={{ color: theme.colors.textSecondary }}>Cargando...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ ...containerStyle, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '16px' }}>
        <p style={{ color: theme.colors.danger }}>{error || 'Error al cargar el pago'}</p>
        <button
          style={{
            padding: '12px 24px',
            backgroundColor: theme.colors.primary,
            color: '#181A1B',
            border: 'none',
            borderRadius: theme.borderRadius.button,
            cursor: 'pointer',
          }}
          onClick={() => navigate('/venues')}
        >
          Volver a Canchas
        </button>
      </div>
    );
  }

  const { booking } = data;
  const totalHours = booking.durationHours;
  const subtotal = booking.totalAmount;

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button style={backButtonStyle} onClick={() => navigate(-1)}>
            <ArrowLeft size={24} />
          </button>

          <div>
            <h1 style={titleStyle}>CONFIRMAR PAGO</h1>
            <p style={subtitleStyle}>Completa tu reserva de forma segura</p>
          </div>
        </div>

        <div style={securityBadgeStyle}>
          <Lock size={16} color={theme.colors.primary} />
          <span style={{ color: theme.colors.primary, fontSize: '13px', fontWeight: theme.fonts.weightBold }}>
            Pago Seguro
          </span>
        </div>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {/* Left Column */}
        <div style={leftColumnStyle}>
          {/* Booking Summary */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>RESUMEN DE RESERVA</h2>

            {/* Venue Info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              {booking.venue.photos && booking.venue.photos.length > 0 && (
                <div
                  style={{
                    width: '120px',
                    height: '90px',
                    borderRadius: theme.borderRadius.button,
                    backgroundImage: `url(${booking.venue.photos[0].url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              )}

              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '20px', fontWeight: theme.fonts.weightBold, color: theme.colors.text, marginBottom: '8px' }}>
                  {booking.venue.name}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.textSecondary, marginBottom: '4px' }}>
                  <MapPin size={14} />
                  <span style={{ fontSize: '14px' }}>{booking.venue.city}, {booking.venue.province}</span>
                </div>
                <p style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                  {booking.venue.venueType.replace('_', ' ')} • {booking.venue.capacity} jugadores
                </p>
              </div>
            </div>

            {/* Booking Details Grid */}
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1, backgroundColor: theme.colors.background, padding: '16px', borderRadius: theme.borderRadius.button }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: theme.colors.primary }}>
                  <Calendar size={16} />
                  <p style={infoLabelStyle}>FECHA</p>
                </div>
                <p style={infoValueStyle}>
                  {new Date(booking.bookingDate).toLocaleDateString('es-AR', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>

              <div style={{ flex: 1, backgroundColor: theme.colors.background, padding: '16px', borderRadius: theme.borderRadius.button }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: theme.colors.primary }}>
                  <Clock size={16} />
                  <p style={infoLabelStyle}>HORARIO</p>
                </div>
                <p style={infoValueStyle}>
                  {booking.startTime} - {booking.endTime}
                </p>
              </div>

              <div style={{ flex: 1, backgroundColor: theme.colors.background, padding: '16px', borderRadius: theme.borderRadius.button }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: theme.colors.primary }}>
                  <Clock size={16} />
                  <p style={infoLabelStyle}>DURACIÓN</p>
                </div>
                <p style={infoValueStyle}>
                  {totalHours} {totalHours === 1 ? 'hora' : 'horas'}
                </p>
              </div>
            </div>
          </div>

          {/* Price Breakdown */}
          <div style={cardStyle}>
            <h2 style={cardTitleStyle}>DETALLE DE PAGO</h2>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ color: theme.colors.textSecondary, fontSize: '15px' }}>
                Alquiler de cancha ({totalHours} {totalHours === 1 ? 'hora' : 'horas'})
              </span>
              <span style={{ color: theme.colors.text, fontSize: '15px' }}>{formatPrice(subtotal)}</span>
            </div>

            <div style={dividerStyle} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '20px', fontWeight: theme.fonts.weightBold, fontFamily: theme.fonts.heading, color: theme.colors.text }}>
                TOTAL
              </span>
              <span style={{ fontSize: '32px', fontWeight: theme.fonts.weightBold, fontFamily: theme.fonts.heading, color: theme.colors.primary }}>
                {formatPrice(booking.totalAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={rightColumnStyle}>
          {/* Mercado Pago Card */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <div style={{ width: '180px', height: '48px', backgroundColor: '#009EE3', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#FFFFFF', fontSize: '24px', fontWeight: 'bold' }}>mercado pago</span>
              </div>
              <p style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>Paga de forma rápida y segura</p>
            </div>

            {/* Payment Methods */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{ ...infoLabelStyle, marginBottom: '12px' }}>Métodos de pago disponibles:</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={methodCardStyle}>
                  <CreditCard size={20} color={theme.colors.primary} />
                  <span style={{ color: theme.colors.text, fontSize: '14px' }}>Tarjeta de Crédito o Débito</span>
                </div>

                <div style={methodCardStyle}>
                  <Wallet size={20} color={theme.colors.primary} />
                  <span style={{ color: theme.colors.text, fontSize: '14px' }}>Dinero en Mercado Pago</span>
                </div>

                <div style={methodCardStyle}>
                  <Smartphone size={20} color={theme.colors.primary} />
                  <span style={{ color: theme.colors.text, fontSize: '14px' }}>Efectivo en puntos de pago</span>
                </div>
              </div>
            </div>

            {/* Pay Button */}
            <button
              style={mpButtonStyle}
              onClick={handlePayWithMercadoPago}
              disabled={processingPayment}
              onMouseEnter={(e) => {
                if (!processingPayment) {
                  e.currentTarget.style.backgroundColor = '#0080B8';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#009EE3';
              }}
            >
              <Lock size={20} />
              <span>{processingPayment ? 'REDIRIGIENDO...' : 'PAGAR CON MERCADO PAGO'}</span>
            </button>

            {/* Features */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px' }}>
              <div style={featureStyle}>
                <CheckCircle size={16} color={theme.colors.primary} />
                <span>Pago 100% seguro y encriptado</span>
              </div>

              <div style={featureStyle}>
                <CheckCircle size={16} color={theme.colors.primary} />
                <span>Protección al comprador</span>
              </div>

              <div style={featureStyle}>
                <CheckCircle size={16} color={theme.colors.primary} />
                <span>Confirmación inmediata</span>
              </div>
            </div>
          </div>

          {/* Trust Section */}
          <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
            <ShieldCheck size={32} color={theme.colors.primary} />
            <p style={{ fontWeight: theme.fonts.weightBold, color: theme.colors.text, fontSize: '14px' }}>
              Tu información está protegida
            </p>
            <p style={{ color: theme.colors.textSecondary, fontSize: '12px' }}>
              No compartimos tus datos de pago con el establecimiento
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
